import { inject, Injectable } from '@angular/core';
import { StorageService } from '@services/common/storage.service';
import * as LaunchDarkly from 'launchdarkly-js-client-sdk';
import { BehaviorSubject, concatMap, Observable, startWith, Subject } from 'rxjs';
import { of } from 'rxjs/internal/observable/of';
import { delay, filter, map, switchMap, take } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class FeatureFlagService {
  private _localStorage = inject(StorageService);

  client: LaunchDarkly.LDClient;
  flags = {};

  private _flagChange$: Subject<{ flag: string; value: any }> = new Subject<{ flag: string; value: any }>();
  flagChange$: Observable<{ flag: string; value: any }> = this._flagChange$.asObservable();

  flags$ = new BehaviorSubject({});

  initialize(user, account) {
    const _user = {
      key: user.id,
      name: user.first_name,
      firstName: user.first_name,
      lastName: user.last_name,
      email: user.email
    };

    const date = new Date(account?.created_at);
    const milliseconds = date.getTime();

    const plan = this._localStorage.getItem('subscription')?.plan?.display_name;

    const _organization = {
      key: account?.id,
      name: user.first_name,
      firstName: user.first_name,
      lastName: user.last_name,
      email: account?.email,
      createdAt: milliseconds,
      planName: plan,
      countryCode: account?.country?.two_letter_code
    };

    const context = {
      kind: 'multi',
      user: _user,
      organization: _organization
    };

    this.client = LaunchDarkly.initialize(environment.LAUNCH_DARCLY_CLIENT_ID, context);

    return this.client;
  }

  onReadyFlagsClient(): void {
    if (!this.client) return;

    this.flags = this.client.allFlags();
    this.flags$.next(this.flags);

    this.client.on(`change`, (flags) => {
      const flagsArray = Object.entries(flags);

      flagsArray.forEach(([flag, value]) => {
        this.setFlag(flag, value['current']);
      });
    });
  }

  hasFlag(flagKey: string) {
    return of(flagKey).pipe(
      concatMap(() => {
        return this.client.waitUntilReady();
      }),
      delay(10),
      switchMap(() => {
        return this.flags$.pipe(
          take(1),
          map((flags) => {
            return flags[flagKey] || false;
          }),
          switchMap((hasFlag) => {
            return this.flagChange$.pipe(
              startWith({ flag: flagKey, value: hasFlag }),
              filter(({ flag }) => flag === flagKey),
              map((res) => res.value)
            );
          })
        );
      })
    );
  }

  setFlag(flagKey, defaultValue) {
    this.flags[flagKey] = defaultValue;
    this.flags$.next(this.flags);

    this._flagChange$.next({
      flag: flagKey,
      value: defaultValue
    });
  }

  constructor() {}
}
